import { mergeDeepRight } from 'ramda';

export const getAllCommitsData = (document, noUnfinished = false) => {
  if (!document || !document.documentId || !Array.isArray(document.commits)) {
    throw new Error('Called without passing a document');
  }

  if (document.commits.length === 0) return {};

  // Sort commits so that latest is last
  let sortedCommits = document.commits.sort(
    (a, b) => (a.commitDate || 10000000000000) - (b.commitDate || 10000000000000),
  );

  // Do not include unfinished commits
  if (noUnfinished) {
    sortedCommits = sortedCommits.filter(d => d.commitDate !== null);
  }

  const mergedCommits = sortedCommits.reduce(
    // Reduce the commits to one document so that the most recent changes overwrite older changes
    (acc, comm) => mergeDeepRight(acc || {}, comm.data),
    {},
  );
  return mergedCommits;
};

export const getLatestCommitData = document => {
  if (!document || !document.documentId || !Array.isArray(document.commits)) {
    throw new Error('Called without passing a document');
  }

  if (document.commits.length === 0) return {};

  const sortedCommits = document.commits.sort(
    (a, b) => (b.commitDate || 10000000000000) - (a.commitDate || 10000000000000),
  );
  if (sortedCommits.length === 0) return {};

  let latestCommit = sortedCommits[0];

  return latestCommit.data || {};
};

export const getLatestUnfinishedCommit = document => {
  if (!document || !document.documentId || !Array.isArray(document.commits)) {
    throw new Error('Called without passing a document');
  }

  if (document.commits.length === 0) {
    throw new Error('Cannot get latest unfinished commit since document contains no commits.');
  }

  const unfinishedCommits = document.commits.filter(c => !c.commitDate);

  if (unfinishedCommits.length === 0) {
    throw new Error('Cannot get latest unfinished commit since document has no unfinished commits.');
  }

  return unfinishedCommits[0];
};

export const getLatestFinishedCommitData = document => {
  if (!document || !document.documentId || !Array.isArray(document.commits)) {
    throw new Error('Called without passing a document');
  }

  if (document.commits.length === 0) return {};

  const finishedCommits = document.commits.filter(c => c.commitDate !== null);
  if (finishedCommits.length === 0) return {};

  const sortedCommits = finishedCommits.sort((a, b) => b.commitDate - a.commitDate);
  const latestCommit = sortedCommits[0];

  return latestCommit.data || {};
};

export const documentIsBeingEdited = document => {
  if (!document || !document.documentId) {
    throw new Error('Called without passing valid document');
  }

  if (document.commits.length === 0) return false;

  return document.commits.some(c => c.commitDate === null);
};

export const findCurrentlyEditedDoc = documents => (documents || []).find(d => documentIsBeingEdited(d));

export const documentHasNoCommits = document => {
  if (!document || !document.documentId) {
    throw new Error('Called without passing valid document');
  }

  if (!Array.isArray(document.commits) || document.commits.length === 0) return true;
  return false;
};

export const getLatestCommitDate = commits => {
  if (commits.length === 0) return null;
  let latest = commits[0].commitDate;
  commits.forEach(c => (c.commitDate > latest ? (latest = c.commitDate) : latest));
  return latest;
};

export const getFirstCommitDate = commits => {
  if (!commits || commits.length === 0) return null;
  let first = commits[0].commitDate;
  commits.forEach(c => (c.commitDate < first ? (first = c.commiDate) : first));
  return first;
};
